import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface Props {
  page: number;
  pageSize: number;
  total: number;
  onPage: (page: number) => void;
  carregando?: boolean;
  /** Ex.: "colaboradores", "notas". */
  rotulo?: string;
}

/** Números das páginas ao redor da atual, com reticências nas pontas. */
function paginasVisiveis(atual: number, total: number): (number | "…")[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const meio = [atual - 1, atual, atual + 1].filter((p) => p > 1 && p < total);
  const lista: (number | "…")[] = [1];
  if (meio[0] > 2) lista.push("…");
  lista.push(...meio);
  if (meio[meio.length - 1] < total - 1) lista.push("…");
  lista.push(total);
  return lista;
}

/** Rodapé de lista paginada no servidor: "1–25 de 120" + navegação. */
export const Paginacao = ({ page, pageSize, total, onPage, carregando, rotulo = "registros" }: Props) => {
  const totalPaginas = Math.max(1, Math.ceil(total / pageSize));
  const inicio = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const fim = Math.min(total, page * pageSize);

  return (
    <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-muted-foreground">
      <span>
        {total === 0 ? "Nenhum resultado" : `${inicio}–${fim} de ${total} ${rotulo}`}
      </span>
      {totalPaginas > 1 && (
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            disabled={page <= 1 || carregando}
            onClick={() => onPage(page - 1)}
            aria-label="Página anterior"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          {paginasVisiveis(page, totalPaginas).map((p, i) =>
            p === "…" ? (
              <span key={`r${i}`} className="px-1">
                …
              </span>
            ) : (
              <Button
                key={p}
                variant={p === page ? "default" : "ghost"}
                size="sm"
                className="h-8 min-w-8 px-2 tabular-nums"
                disabled={carregando && p !== page}
                onClick={() => onPage(p)}
              >
                {p}
              </Button>
            ),
          )}
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            disabled={page >= totalPaginas || carregando}
            onClick={() => onPage(page + 1)}
            aria-label="Próxima página"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
};
